import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import { Indent } from "./extensions/Indent";
import { useEffect, useRef } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  IndentDecrease,
  IndentIncrease,
  Italic,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo,
  Strikethrough,
  Undo,
} from "lucide-react";

type ToolbarVariant = "minimal" | "full";

type Props = {
  content: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  minHeight?: number;
  toolbarVariant?: ToolbarVariant;
  readOnly?: boolean;
};

const TOOLBAR_BTN =
  "flex h-[26px] w-[26px] items-center justify-center rounded border-none bg-transparent cursor-pointer text-[hsl(215,16%,47%)] transition-colors hover:bg-[hsl(210,50%,92%)] hover:text-[hsl(210,100%,28%)]";
const TOOLBAR_BTN_ACTIVE =
  "flex h-[26px] w-[26px] items-center justify-center rounded border-none cursor-pointer transition-colors bg-[hsl(210,55%,88%)] text-[hsl(210,100%,25%)]";

function ToolbarButton({
  action,
  isActive,
  icon: Icon,
  title,
  disabled = false,
}: {
  action: () => void;
  isActive: boolean;
  icon: typeof Bold;
  title: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={action}
      disabled={disabled}
      className={
        (isActive ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN) +
        " disabled:cursor-not-allowed disabled:opacity-40"
      }
      title={title}
    >
      <Icon className="h-[13px] w-[13px]" strokeWidth={1.5} />
    </button>
  );
}

function MinimalToolbar({ editor }: { editor: Editor }) {
  return (
    <>
      <ToolbarButton
        action={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        icon={Bold}
        title="Bold"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        icon={Italic}
        title="Italic"
      />
      <div className="mx-1 h-4 w-px bg-[hsl(210,30%,85%)]" />
      <ToolbarButton
        action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
        icon={Heading1}
        title="Heading 1"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
        icon={Heading2}
        title="Heading 2"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive("heading", { level: 3 })}
        icon={Heading3}
        title="Heading 3"
      />
      <div className="mx-1 h-4 w-px bg-[hsl(210,30%,85%)]" />
      <ToolbarButton
        action={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        icon={List}
        title="Bullet list"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        icon={ListOrdered}
        title="Numbered list"
      />
      <ToolbarButton
        action={() => editor.chain().focus().indent().run()}
        isActive={false}
        disabled={!editor.can().chain().focus().indent().run()}
        icon={IndentIncrease}
        title="Indent paragraph"
      />
      <ToolbarButton
        action={() => editor.chain().focus().outdent().run()}
        isActive={false}
        disabled={!editor.can().chain().focus().outdent().run()}
        icon={IndentDecrease}
        title="Outdent paragraph"
      />
    </>
  );
}

function FullToolbar({ editor }: { editor: Editor }) {
  return (
    <>
      <ToolbarButton
        action={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        icon={Bold}
        title="Bold"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        icon={Italic}
        title="Italic"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")}
        icon={Strikethrough}
        title="Strikethrough"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleCode().run()}
        isActive={editor.isActive("code")}
        icon={Code}
        title="Inline code"
      />
      <div className="mx-1 h-4 w-px bg-[hsl(210,30%,85%)]" />
      <ToolbarButton
        action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
        icon={Heading1}
        title="Heading 1"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
        icon={Heading2}
        title="Heading 2"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive("heading", { level: 3 })}
        icon={Heading3}
        title="Heading 3"
      />
      <div className="mx-1 h-4 w-px bg-[hsl(210,30%,85%)]" />
      <ToolbarButton
        action={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        icon={List}
        title="Bullet list"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        icon={ListOrdered}
        title="Numbered list"
      />
      <ToolbarButton
        action={() => editor.chain().focus().indent().run()}
        isActive={false}
        disabled={!editor.can().chain().focus().indent().run()}
        icon={IndentIncrease}
        title="Indent paragraph"
      />
      <ToolbarButton
        action={() => editor.chain().focus().outdent().run()}
        isActive={false}
        disabled={!editor.can().chain().focus().outdent().run()}
        icon={IndentDecrease}
        title="Outdent paragraph"
      />
      <ToolbarButton
        action={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive("blockquote")}
        icon={Quote}
        title="Blockquote"
      />
      <div className="mx-1 h-4 w-px bg-[hsl(210,30%,85%)]" />
      <ToolbarButton
        action={() => editor.chain().focus().setTextAlign("left").run()}
        isActive={editor.isActive({ textAlign: "left" })}
        icon={AlignLeft}
        title="Align left"
      />
      <ToolbarButton
        action={() => editor.chain().focus().setTextAlign("center").run()}
        isActive={editor.isActive({ textAlign: "center" })}
        icon={AlignCenter}
        title="Align center"
      />
      <ToolbarButton
        action={() => editor.chain().focus().setTextAlign("right").run()}
        isActive={editor.isActive({ textAlign: "right" })}
        icon={AlignRight}
        title="Align right"
      />
      <div className="mx-1 h-4 w-px bg-[hsl(210,30%,85%)]" />
      <ToolbarButton
        action={() => editor.chain().focus().setHorizontalRule().run()}
        isActive={false}
        icon={Minus}
        title="Horizontal rule"
      />
      <ToolbarButton
        action={() => editor.chain().focus().undo().run()}
        isActive={false}
        icon={Undo}
        title="Undo"
      />
      <ToolbarButton
        action={() => editor.chain().focus().redo().run()}
        isActive={false}
        icon={Redo}
        title="Redo"
      />
    </>
  );
}

export function RichTextEditor({
  content,
  onChange,
  onBlur,
  placeholder = "",
  minHeight = 200,
  toolbarVariant = "minimal",
  readOnly = false,
}: Props) {
  const skipNextUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Typography,
      Indent.configure({
        types: ["paragraph", "heading"],
        minLevel: 0,
        maxLevel: 4,
        stepRem: 2,
      }),
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor: e }) => {
      if (skipNextUpdate.current) {
        skipNextUpdate.current = false;
        return;
      }
      onChange(e.getHTML());
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  // Sync external content changes
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      skipNextUpdate.current = true;
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  if (!editor) return null;

  return (
    <div
      className={
        readOnly
          ? "overflow-hidden"
          : "overflow-hidden rounded-xl border bg-white"
      }
      style={readOnly ? undefined : { borderColor: "hsl(210,30%,85%)" }}
    >
      {/* Toolbar */}
      {!readOnly && (
        <div
          className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5"
          style={{
            borderColor: "hsl(210,30%,85%)",
            background: "hsl(210,30%,98%)",
          }}
        >
          {toolbarVariant === "minimal" ? (
            <MinimalToolbar editor={editor} />
          ) : (
            <FullToolbar editor={editor} />
          )}
        </div>
      )}

      {/* Editor content */}
      <div
        style={
          readOnly
            ? { minHeight }
            : { minHeight, resize: "vertical", overflow: "auto" }
        }
      >
        <EditorContent
          editor={editor}
          className={
            readOnly
              ? "tiptap-editor tiptap-readonly rich-text-content"
              : "tiptap-editor rich-text-content"
          }
          style={{ minHeight }}
        />
      </div>

      <style>{`
        .tiptap-editor .tiptap {
          padding: 12px 16px;
          min-height: ${minHeight}px;
          font-size: 14px;
          line-height: 1.6;
          color: hsl(215, 25%, 15%);
          outline: none;
        }
        .tiptap-editor .tiptap p {
          margin-bottom: 8px;
        }
        .tiptap-editor .tiptap h1 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .tiptap-editor .tiptap h2 {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 6px;
        }
        .tiptap-editor .tiptap h3 {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .tiptap-editor .tiptap blockquote {
          border-left: 3px solid hsl(210, 68%, 55%);
          padding-left: 12px;
          color: hsl(215, 16%, 47%);
          margin-bottom: 8px;
        }
        .tiptap-editor .tiptap hr {
          border: none;
          border-top: 1px solid hsl(210, 30%, 85%);
          margin: 12px 0;
        }
        .tiptap-editor .tiptap code {
          background: hsl(210, 16%, 93%);
          border-radius: 3px;
          padding: 1px 4px;
          font-size: 13px;
        }
        .tiptap-readonly .tiptap {
          padding: 0;
          background: transparent;
          caret-color: transparent;
        }
        .tiptap-readonly .tiptap ::selection {
          background: transparent;
        }
        .tiptap-editor .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: hsl(215, 16%, 60%);
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
}
